import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Globals } from '../global/globals';
import { GroupCreateUpdate } from '../dtos/group-create-update';
import { Group } from '../dtos/group';
import { Member } from '../dtos/member';
import { JoinRequest } from '../dtos/join-request';
import { PaginatedResponse } from '../dtos/paginated';
import { Project } from '../dtos/project';

@Injectable({
  providedIn: 'root',
})
export class GroupService {
  private baseUri = this.globals.backendUri + '/groups';

  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
  ) {}

  createGroup(request: GroupCreateUpdate): Observable<any> {
    console.log('Create group', request.name);
    return this.httpClient.post<any>(this.baseUri, request);
  }

  requestJoinByInviteToken(inviteToken: string): Observable<any> {
    return this.httpClient.post<any>(
      `${this.baseUri}/join?token=${encodeURIComponent(inviteToken)}`,
      {},
    );
  }

  getGroupInfoByInviteToken(inviteToken: string): Observable<Group> {
    return this.httpClient
      .get<any>(`${this.baseUri}/join?token=${encodeURIComponent(inviteToken)}`)
      .pipe(
        map(
          (group) =>
            new Group(
              group.id?.toString() ?? '',
              group.name,
              group.inviteToken ?? '',
              group.inviteActive ?? false,
              group.createdAt ?? '',
              group.ownerUserId != null ? String(group.ownerUserId) : '',
              group.description,
            ),
        ),
      );
  }

  getGroups(): Observable<Group[]> {
    return this.httpClient
      .get<any>(this.baseUri)
      .pipe(
        map((res) =>
          (res.items ?? []).map(
            (group: any) =>
              new Group(
                group.id?.toString() ?? '',
                group.name,
                group.inviteToken ?? '',
                group.inviteActive ?? false,
                group.createdAt ?? '',
                group.ownerUserId != null ? String(group.ownerUserId) : '',
                group.description,
                group.memberCount ?? 0,
                group.projectCount ?? 0,
              ),
          ),
        ),
      );
  }

  getGroupsPaginated(page: number, size: number, q?: string): Observable<PaginatedResponse<Group>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (q) {
      params = params.set('q', q);
    }
    return this.httpClient.get<any>(this.baseUri, { params }).pipe(
      map((res) => ({
        items: (res.items ?? []).map(
          (group: any) =>
            new Group(
              group.id?.toString() ?? '',
              group.name,
              group.inviteToken ?? '',
              group.inviteActive ?? false,
              group.createdAt ?? '',
              group.ownerUserId != null ? String(group.ownerUserId) : '',
              group.description,
              group.memberCount ?? 0,
              group.projectCount ?? 0,
            ),
        ),
        totalItems: res.totalItems,
        totalPages: res.totalPages,
        currentPage: res.currentPage,
        pageSize: res.pageSize,
      })),
    );
  }

  getGroupById(groupId: string): Observable<Group> {
    return this.getGroups().pipe(
      map((groups) => {
        const group = groups.find((g) => String(g.id) === String(groupId));
        if (!group) {
          throw new Error('Group not found');
        }
        return group;
      }),
    );
  }

  updateGroup(groupId: string, request: GroupCreateUpdate): Observable<Group> {
    return this.httpClient
      .put<any>(`${this.baseUri}/${groupId}`, request)
      .pipe(
        map(
          (group) =>
            new Group(
              group.id?.toString() ?? '',
              group.name,
              group.inviteToken ?? '',
              group.inviteActive ?? false,
              group.createdAt ?? '',
              group.ownerUserId != null ? String(group.ownerUserId) : '',
              group.description,
              group.memberCount ?? 0,
              group.projectCount ?? 0,
            ),
        ),
      );
  }

  deleteGroup(groupId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUri}/${groupId}`);
  }

  leaveGroup(groupId: string): Observable<void> {
    return this.httpClient.post<void>(`${this.baseUri}/${groupId}/leave`, {});
  }

  getGroupMembers(
    groupId: string,
    page: number,
    size: number,
    q?: string,
  ): Observable<{
    page: PaginatedResponse<Member>;
    currentUserRole: string | null;
  }> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (q) {
      params = params.set('q', q);
    }
    return this.httpClient.get<any>(`${this.baseUri}/${groupId}/members`, { params }).pipe(
      map((res) => ({
        page: {
          items: (res.items ?? []).map(
            (m: any) => new Member(m.user, m.groupRole, m.joinedAt ?? ''),
          ),
          totalItems: res.totalItems,
          totalPages: res.totalPages,
          currentPage: res.currentPage,
          pageSize: res.pageSize,
        },
        currentUserRole: res.currentUserRole ?? null,
      })),
    );
  }

  getAllGroupMembers(groupId: string): Observable<Member[]> {
    return this.getGroupMembers(groupId, 0, 100).pipe(map((res) => res.page.items));
  }

  getGroupJoinRequests(groupId: string): Observable<JoinRequest[]> {
    return this.httpClient
      .get<any[]>(`${this.baseUri}/requests`)
      .pipe(
        map((requests) =>
          requests
            .filter((r) => String(r.researchGroupId ?? '') === String(groupId))
            .map(
              (r) =>
                new JoinRequest(
                  r.id,
                  r.researchGroupId,
                  r.researchGroupName,
                  r.userId,
                  r.userEmail,
                  r.firstName,
                  r.lastName,
                  r.status,
                  r.createdAt ?? '',
                ),
            ),
        ),
      );
  }

  approveJoinRequest(groupId: string, requestId: number, role?: string): Observable<any> {
    return this.httpClient.post<any>(`${this.baseUri}/requests/${requestId}`, {
      accept: true,
      role: role || null,
    });
  }

  rejectJoinRequest(groupId: string, requestId: number): Observable<any> {
    return this.httpClient.post<any>(`${this.baseUri}/requests/${requestId}`, { accept: false });
  }

  removeMember(groupId: string, userId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUri}/${groupId}/members/${userId}`);
  }

  changeMemberRole(groupId: string, userId: string, role: string): Observable<void> {
    return this.httpClient.put<void>(`${this.baseUri}/${groupId}/members/${userId}`, { role });
  }

  getGroupProjects(groupId: string): Observable<Project[]> {
    return this.httpClient
      .get<any[]>(`${this.baseUri}/${groupId}/projects`)
      .pipe(
        map((projects) =>
          projects.map(
            (project) =>
              new Project(
                project.id?.toString() ?? '',
                project.title ?? '',
                project.description ?? '',
                project.groupId?.toString() ?? '',
                project.groupName ?? '',
                project.piUserId?.toString() ?? '',
                project.piName ?? '',
                project.state ?? 'SETUP',
                project.createdAt ?? '',
                project.completedAt ?? '',
                project.userRole ?? 'NONE',
                project.paperCount ?? 0,
                project.exclusionCriteria ?? '',
                project.cohenKappaThreshold ?? null,
              ),
          ),
        ),
      );
  }
}
